import { useState, useEffect } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import { NAV } from "@/data/demo";
import * as Icons from "lucide-react";

const ACTIONS = [
  { label: "Ask MFDX AI Copilot", to: "/app/ai", icon: "Sparkles" },
  { label: "What should I do today?", to: "/app", icon: "ListTodo" },
  { label: "Build a campaign (Marketing Factory)", to: "/app/marketing", icon: "Megaphone" },
  { label: "Create content pack (Content Studio)", to: "/app/content", icon: "PenSquare" },
  { label: "View my clients (Client 360)", to: "/app/clients", icon: "UserSquare2" },
  { label: "Leads I should call today", to: "/app/leads", icon: "Target" },
  { label: "This week's brief", to: "/app/weekly", icon: "CalendarCheck" },
  { label: "Research & compare funds", to: "/app/funds", icon: "LineChart" },
];

// Natural-language intents → destination
const INTENTS = [
  { kw: ["invest", "6 month", "six month", "inactive", "dormant", "haven't", "not contacted", "quiet", "lapsed"], label: "Clients who haven't invested / gone quiet", to: "/app/retention", icon: "MoonStar" },
  { kw: ["risk", "sip fail", "bounce", "failed", "at risk", "stopped"], label: "SIPs at risk & retention", to: "/app/retention", icon: "AlertTriangle" },
  { kw: ["hni", "top client", "big client", "wealthy"], label: "My clients (Client 360)", to: "/app/clients", icon: "UserSquare2" },
  { kw: ["lead", "prospect", "follow up", "followup", "call", "pipeline"], label: "Leads & follow-ups", to: "/app/leads", icon: "Target" },
  { kw: ["task", "to-do", "todo", "reminder"], label: "My tasks for today", to: "/app/tasks", icon: "ListChecks" },
  { kw: ["whatsapp", "post", "reel", "content", "message", "linkedin", "instagram", "email"], label: "Create content / WhatsApp message", to: "/app/content", icon: "Wand2" },
  { kw: ["campaign", "acquire", "market", "get client", "grow"], label: "Marketing Factory — run a campaign", to: "/app/marketing", icon: "Megaphone" },
  { kw: ["sebi", "amfi", "rbi", "regul", "compliance", "circular"], label: "Regulatory Hub — what changed", to: "/app/regulatory", icon: "ShieldCheck" },
  { kw: ["fund", "compare", "nfo", "scheme", "research"], label: "Fund Research & comparison", to: "/app/funds", icon: "LineChart" },
  { kw: ["market", "nifty", "sensex", "talking point", "brief"], label: "Market Intelligence", to: "/app/market", icon: "TrendingUp" },
  { kw: ["score", "growth", "how am i", "performance", "business"], label: "My business & Growth Score", to: "/app/business", icon: "Gauge" },
  { kw: ["week", "monday", "plan"], label: "Your Weekly Brief", to: "/app/weekly", icon: "CalendarCheck" },
  { kw: ["community", "peer", "discuss", "poll"], label: "MFD Community", to: "/app/community", icon: "MessageSquare" },
];

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

const match = (list, q) => list.filter((a) => a.label.toLowerCase().includes(q));

export default function CommandBar({ open, setOpen }) {
  const nav = useNavigate();
  const [value, setValue] = useState("");
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, setOpen]);
  if (!open) return null;
  const go = (to, state) => { setOpen(false); setValue(""); nav(to, state ? { state } : undefined); };

  const q = value.trim().toLowerCase();
  const smart = q ? INTENTS.filter((it) => it.kw.some((k) => q.includes(k) || k.includes(q))) : [];
  const actions = q ? match(ACTIONS, q) : ACTIONS;
  const navItems = q ? match(NAV, q) : NAV;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={() => setOpen(false)}>
      <div className="w-full max-w-xl card-mfx overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="command-palette">
        <Command shouldFilter={false} className="[&_[cmdk-input]]:outline-none">
          <div className="flex items-center gap-2 px-4 border-b border-[#222938]">
            <Icon name="Sparkles" size={18} className="text-amber-400" />
            <Command.Input value={value} onValueChange={setValue} autoFocus placeholder="What do you want to get done? e.g. clients who haven't invested in 6 months" className="flex-1 py-4 text-sm bg-transparent text-slate-100 placeholder:text-slate-500" data-testid="command-input" />
            <kbd className="text-[11px] bg-[#141822] border border-[#2E384D] rounded px-1.5 py-0.5 text-slate-400">ESC</kbd>
          </div>
          <Command.List className="max-h-96 overflow-y-auto p-2">
            {q && (
              <Command.Group heading={smart.length ? "Smart results" : "Ask MFDX Copilot"} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-amber-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                {smart.map((s) => (
                  <Command.Item key={s.label} value={`smart-${s.label}`} onSelect={() => go(s.to)} data-testid="cmd-smart-result"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer text-slate-200 data-[selected=true]:bg-amber-500/12 data-[selected=true]:text-amber-300">
                    <Icon name={s.icon} size={16} className="text-amber-400 shrink-0" /> {s.label}
                  </Command.Item>
                ))}
                <Command.Item value="ask-ai-fallback" onSelect={() => go("/app/ai", { prompt: value.trim() })} data-testid="cmd-ask-ai"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer text-slate-200 data-[selected=true]:bg-amber-500/12 data-[selected=true]:text-amber-300">
                  <Icon name="Sparkles" size={16} className="text-amber-400 shrink-0" /> Ask MFDX Copilot: “{value.trim()}”
                </Command.Item>
              </Command.Group>
            )}
            {actions.length > 0 && (
              <Command.Group heading="Quick Actions" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-slate-500 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                {actions.map((a) => (
                  <Command.Item key={a.label} value={a.label} onSelect={() => go(a.to)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer text-slate-300 data-[selected=true]:bg-amber-500/12 data-[selected=true]:text-amber-300">
                    <Icon name={a.icon} size={16} className="text-slate-500 shrink-0" /> {a.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {navItems.length > 0 && (
              <Command.Group heading="Navigate" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-slate-500 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                {navItems.map((n) => (
                  <Command.Item key={n.key} value={`nav-${n.label}`} onSelect={() => go(n.path)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer text-slate-300 data-[selected=true]:bg-amber-500/12 data-[selected=true]:text-amber-300">
                    <Icon name={n.icon} size={16} className="text-slate-500 shrink-0" /> {n.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {q && smart.length === 0 && actions.length === 0 && navItems.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">Press Enter on “Ask Copilot” above to get an answer.</div>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
